import useSWR from 'swr';
import { Project } from '@/lib/api';
import type { DirectionId } from '@/lib/labDirectionTags';

interface ProjectsParams {
  limit?: number;
  offset?: number;
  status?: string;
  search?: string;
  ordering?: string;
  is_public?: boolean;
}

// Генерация ключа для SWR
function getProjectsKey(params: ProjectsParams, endpoint: string): string {
  const query = new URLSearchParams();
  if (params.limit) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));
  if (params.status) query.set('status', params.status);
  if (params.search) query.set('search', params.search);
  if (params.ordering) query.set('ordering', params.ordering);
  if (params.is_public !== undefined) query.set('is_public', String(params.is_public));

  const queryString = query.toString();
  return queryString ? `${endpoint}?${queryString}` : endpoint;
}

// Хук для публичных проектов лаборатории
export function usePublicProjects(limit: number = 10, direction?: DirectionId) {
  const query = new URLSearchParams({ limit: String(limit) });
  if (direction) query.set('direction', direction);
  const { data, error, isLoading, isValidating, mutate } = useSWR<Project[]>(
    `/api/projects/public/?${query}`,
    {
      refreshInterval: 120000, // Раз в 2 минуты
      revalidateIfStale: true,
      keepPreviousData: false,
    }
  );

  return {
    projects: Array.isArray(data) ? data : [],
    isLoading,
    isValidating,
    error,
    mutate,
  };
}

// Хук для публичных проектов пользователей
export function usePublicUserProjects(limit: number = 10) {
  const { data, error, isLoading, isValidating, mutate } = useSWR<Project[]>(
    `/api/projects/public/users/?limit=${limit}`,
    {
      refreshInterval: 120000,
      revalidateIfStale: true,
    }
  );

  return {
    projects: Array.isArray(data) ? data : [],
    isLoading,
    isValidating,
    error,
    mutate,
  };
}

// Хук для проектов текущего пользователя
export function useMyProjects(params: ProjectsParams = {}) {
  const key = getProjectsKey(params, '/api/projects/');

  const { data, error, isLoading, isValidating, mutate } = useSWR<Project[] | { results: Project[]; count: number }>(
    key,
    {
      refreshInterval: 60000,
      revalidateIfStale: true,
    }
  );

  const projects = (() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.results || [];
  })();

  return {
    projects,
    isLoading,
    isValidating,
    error,
    mutate,
  };
}

// Хук для одного проекта
export function useProject(projectId: number | null) {
  const { data, error, isLoading, isValidating, mutate } = useSWR<Project>(
    projectId ? `/api/projects/${projectId}/` : null,
    {
      revalidateIfStale: true,
    }
  );

  return {
    project: data ?? null,
    isLoading,
    isValidating,
    error,
    mutate,
  };
}
